import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trainingApi } from '../../api/training';
import { TrainingRequest } from '../../types/training';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { RecordDetailModal } from '../../components/RecordDetailModal';
import { ReturnAction } from '../../components/ReturnAction';
import { useToast } from '../../components/Toast';

/** Only shows requests already approved by the employee's real supervisor - the final, role-based HR step. */
export function TrainingReviewPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [certificateNotes, setCertificateNotes] = useState<Record<number, string>>({});
  const [viewingRequest, setViewingRequest] = useState<TrainingRequest | null>(null);
  const pendingQuery = useQuery({ queryKey: ['training-pending-hr'], queryFn: () => trainingApi.pendingForHR() });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['training-pending-hr'] });
  const decideMutation = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) => trainingApi.hrDecide(id, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const completeMutation = useMutation({
    mutationFn: (id: number) => trainingApi.complete(id, { certificateNotes: certificateNotes[id] }),
    onSuccess: () => { showSuccess('Marked completed.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <h1>Training - HR Review</h1>
      <p className="muted">Requests already approved by the employee's supervisor, awaiting final HR approval or completion.</p>
      {pendingQuery.data && (
        <DataTable
          rows={pendingQuery.data.data}
          rowKey={(r) => r.id}
          emptyMessage="Nothing pending."
          columns={[
            { header: 'Employee', render: (r) => r.employee_name },
            { header: 'Title', render: (r) => r.catalogue_title ?? r.training_title ?? '-' },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
            {
              header: '',
              render: (r) => (
                <span className="row-actions">
                  <button className="link-button" onClick={() => setViewingRequest(r)}>View</button>
                  {r.status === 'under_review' && (
                    <>
                      <button className="link-button" onClick={() => decideMutation.mutate({ id: r.id, decision: 'approved' })}>Approve</button>
                      <button className="link-button" onClick={() => decideMutation.mutate({ id: r.id, decision: 'rejected' })}>Reject</button>
                      <ReturnAction onReturn={(comment) => trainingApi.hrReturn(r.id, comment)} queryKeysToInvalidate={[['training-pending-hr']]} />
                    </>
                  )}
                  {r.status === 'approved' && (
                    <>
                      <input placeholder="Certificate notes" value={certificateNotes[r.id] ?? ''} onChange={(e) => setCertificateNotes((prev) => ({ ...prev, [r.id]: e.target.value }))} />
                      <button className="link-button" onClick={() => completeMutation.mutate(r.id)}>Mark Completed</button>
                    </>
                  )}
                </span>
              ),
            },
          ]}
        />
      )}

      {viewingRequest && (
        <RecordDetailModal
          title={`Training Request - ${viewingRequest.employee_name ?? ''}`}
          onClose={() => setViewingRequest(null)}
          fields={[
            { label: 'Status', value: <StatusBadge status={viewingRequest.status} /> },
            { label: 'Title', value: viewingRequest.catalogue_title ?? viewingRequest.training_title },
            { label: 'Provider', value: viewingRequest.training_provider },
            { label: 'Proposed Date', value: viewingRequest.proposed_date },
            { label: 'Supervisor Decision', value: viewingRequest.supervisor_decision },
            { label: 'Reason / Justification', value: viewingRequest.reason, fullWidth: true },
            { label: 'Return Comment', value: viewingRequest.return_comment, fullWidth: true },
          ]}
        />
      )}
    </div>
  );
}
